// Active-section tracking for the header navigation.
// Ported from the production hotfix `navigation-88045a9aa983.js` (deployed 2026-09-24 on top of 289d7c1).
// React remains the sole owner of the active class and aria-current.
const ids = ['about', 'capabilities', 'system', 'work', 'careers', 'contact']
let pending: { id: string; until: number } | null = null
let activate: ((id: string) => void) | null = null

/** Pin the clicked item as active until smooth scrolling reaches it (or 2.2 s pass). */
export function selectNavigation(id: string) {
  pending = { id, until: performance.now() + 2200 }
  activate?.(id)
}

/** The active section is the last one whose top has crossed the line just under the header. */
export function trackNavigation(setActive: (id: string) => void) {
  activate = setActive
  let frame = 0
  const update = () => {
    frame = 0
    const sections = ids.map((id) => document.getElementById(id)).filter((section): section is HTMLElement => Boolean(section))
    if (!sections.length) return
    const header = document.querySelector('.site-header')
    const line = Math.max(0, header?.getBoundingClientRect().bottom || 80) + 24
    const bottom = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 3
    let current = sections[0].id
    for (const section of sections) {
      if (section.getBoundingClientRect().top <= line) current = section.id
    }
    if (bottom && window.scrollY > 0) current = sections[sections.length - 1].id
    if (pending) {
      const target = document.getElementById(pending.id)
      const reached = current === pending.id && (bottom || (target && target.getBoundingClientRect().top <= line))
      if (reached || performance.now() >= pending.until) pending = null
      else current = pending.id
    }
    setActive(current)
    if (pending) frame = requestAnimationFrame(update)
  }
  const schedule = () => { if (!frame) frame = requestAnimationFrame(update) }
  const interrupt = () => { pending = null; schedule() }
  const key = (event: KeyboardEvent) => { if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) interrupt() }
  window.addEventListener('scroll', schedule, { passive: true })
  window.addEventListener('resize', schedule)
  window.addEventListener('wheel', interrupt, { passive: true })
  window.addEventListener('touchstart', interrupt, { passive: true })
  window.addEventListener('keydown', key)
  const resize = new ResizeObserver(schedule)
  resize.observe(document.documentElement)
  const mutation = new MutationObserver(schedule)
  mutation.observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] })
  schedule()
  return () => {
    cancelAnimationFrame(frame)
    resize.disconnect()
    mutation.disconnect()
    window.removeEventListener('scroll', schedule)
    window.removeEventListener('resize', schedule)
    window.removeEventListener('wheel', interrupt)
    window.removeEventListener('touchstart', interrupt)
    window.removeEventListener('keydown', key)
    activate = null
    pending = null
  }
}
